import { useRef, useState } from "react";
import { uploadFile, ACCEPT, MAX_SIZE, SIZE_LABEL } from "../services/supabase";

/**
 * FileUploader — botón de subida reutilizable
 *
 * Props:
 *   folder:      'images' | 'pdfs' | 'resources'
 *   onUploaded:  (url: string) => void
 *   label:       string
 *   currentUrl:  string
 *   compact:     boolean
 */
export default function FileUploader({
  folder = "images",
  onUploaded,
  label,
  currentUrl,
  compact = false,
}) {
  const inputRef                  = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState("");
  const [preview,   setPreview]   = useState(currentUrl || "");

  const isImage = folder === "images";

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_SIZE[folder]) {
      setError(`El archivo supera el límite de ${SIZE_LABEL[folder]}.`);
      return;
    }

    setError("");
    setUploading(true);

    try {
      const url = await uploadFile(file, folder);
      setPreview(url);
      onUploaded(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const clearFile = () => { setPreview(""); onUploaded(""); };

  const buttonLabel = label || {
    images:    "📷 Subir imagen",
    pdfs:      "📄 Subir PDF",
    resources: "📎 Subir archivo",
  }[folder];

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:".5rem" }}>

      {/* Preview imagen */}
      {isImage && preview && !compact && (
        <div style={{ position:"relative", width:"100%", maxWidth:280 }}>
          <img
            src={preview}
            alt="preview"
            style={{ width:"100%", height:140, objectFit:"cover", borderRadius:8, border:"0.5px solid var(--border)", display:"block" }}
            onError={() => setPreview("")}
          />
          <button
            type="button"
            onClick={clearFile}
            style={{ position:"absolute", top:6, right:6, background:"rgba(0,0,0,.55)", border:"none", color:"#fff", width:22, height:22, borderRadius:"50%", fontSize:"0.7rem", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}
          >✕</button>
        </div>
      )}

      {/* Preview PDF / archivo */}
      {!isImage && preview && !compact && (
        <div style={{ display:"flex", alignItems:"center", gap:".6rem", background:"var(--teal-light)", borderRadius:6, padding:".5rem .8rem" }}>
          <span style={{ fontSize:"1.1rem" }}>{folder === "pdfs" ? "📄" : "📎"}</span>
          <a
            href={preview}
            target="_blank"
            rel="noreferrer"
            style={{ fontSize:"0.78rem", color:"var(--teal)", fontFamily:"var(--font-display)", fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1 }}
          >
            Ver archivo subido ↗
          </a>
          <button type="button" onClick={clearFile} style={{ background:"none", border:"none", color:"#c0392b", cursor:"pointer", fontSize:"0.8rem", flexShrink:0 }}>✕</button>
        </div>
      )}

      {/* Botón subida */}
      <div style={{ display:"flex", alignItems:"center", gap:".6rem", flexWrap:"wrap" }}>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT[folder]}
          onChange={handleFile}
          style={{ display:"none" }}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          style={{
            fontFamily:"var(--font-display)",
            fontWeight:700,
            fontSize: compact ? "0.72rem" : "0.78rem",
            background: uploading ? "var(--border)" : "var(--teal-light)",
            color: uploading ? "var(--text-muted)" : "var(--teal-dark)",
            border:"1.5px solid var(--teal)",
            padding: compact ? "4px 12px" : "6px 16px",
            borderRadius:6,
            cursor: uploading ? "not-allowed" : "pointer",
            transition:"background .15s",
            display:"flex",
            alignItems:"center",
            gap:".4rem",
          }}
        >
          {uploading ? (
            <>
              <span style={{ display:"inline-block", width:12, height:12, border:"2px solid var(--teal)", borderTopColor:"transparent", borderRadius:"50%", animation:"spin .7s linear infinite" }} />
              Subiendo...
            </>
          ) : buttonLabel}
        </button>

        {compact && preview && (
          <span style={{ fontSize:"0.7rem", color:"var(--teal)", fontFamily:"var(--font-display)", fontWeight:600 }}>✓ Subido</span>
        )}

        <span style={{ fontSize:"0.68rem", color:"var(--text-light)" }}>Máx. {SIZE_LABEL[folder]}</span>
      </div>

      {error && <p style={{ fontSize:"0.75rem", color:"#c0392b", margin:0 }}>{error}</p>}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}